<template>
  <popup
    :opened="dataToShow"
    @close="changeToShow(false)"
  >
    <message-header :message-header="messageHeader" />

    <!-- BEGIN Content modal add beneficiary to a referent -->
    <div class="my-5 lg:my-15 2xl:my-20">
      <h3 class="mb-20">
        Ajouter un bénéficiaire à gérer
      </h3>

      <!-- BEGIN add new + search + sort -->
      <div
        class="flex content-center items-center justify-between my-10 mx-5 lg:mx-15 2xl:mx-20"
      >
        <div class="flex flex-col items-center justify-center mt-5 w-full">
          <div class="flex items-center justify-between w-full px-5 mb-2">
            <div class="w-full">
              <search-bar
                :value="searchBeneficiary"
                placeholder="Rechercher un bénéficiaire"
                :big="false"
                :inherits="false"
                :has-border="false"
                @input="(newValue) => {searchBeneficiary = newValue}"
              />
            </div>
          </div>
          <!-- BEGIN Search engine and alphabetic order -->
          <div class="w-full px-5 flex items-center justify-end">
            <sort-button
              :sort="sort"
              @sorted="
                (sortType) => {
                  sort = sortType;
                }
              "
            />
          </div>
          <!-- END Search engine and alphabetic order -->
        </div>
      </div>
      <!-- END add new + search + sort -->

      <list-grid
        :list-items="beneficiariesPaginated.filter(_ => !saving)"
        grid="grid-cols-6 md:grid-cols-12"
      >
        <template
          #header
          v-if="beneficiariesToAdd.length > 0"
        >
          <div class="col-start-1 lg:col-start-2 col-span-1 justify-self-start">
            <checkbox-indigo
              name="Tous"
              libelle="Tous"
              :value="
                beneficiariesToAddSort.length === selectedBeneficiaries.length
              "
              @input="selectAllBeneficiaries()"
              :item-list="beneficiariesToAddSort"
              :selected-items="selectedBeneficiaries"
              color-libelle="opacity-0 md:opacity-100 indigo-600"
              size-libelle="text-lg"
              class="text-gray-900"
            />
          </div>
          <div class="hidden md:inline col-span-1" /> <!-- Profile Pic -->
          <div class=" col-span-3 md:col-span-5 lg:col-span-4">
            Bénéficiaires
          </div>
          <div class="col-span-2 md:col-span-4">
            Rôles
          </div>
        </template>
        <template #accordionDescription="{item, num}">
          <div class="col-start-1 lg:col-start-2 col-span-1 justify-self-start">
            <checkbox-indigo
              :value="
                selectedBeneficiaries.filter((b) => b.id === item.id)
                  .length === 1
              "
              @input="selectBeneficiary(item)"
            />
          </div>
          <div class="hidden md:inline col-span-1">
            <ProfilePicture
              size="max-w-10 w-10 h-10"
              :other-object="item"
            />
          </div>
          <div class="col-span-3 md:col-span-5 lg:col-span-4 text-lg justify-self-start">
            {{ item.firstname }}
            <span class="uppercase">{{ item.lastname }}</span>
          </div>

          <div class="col-span-2 md:col-span-4 justify-self-start">
            <input-select
              :name="'role' + num"
              :datas="listRoles"
              :value="item.role"
              @input="
                (newValue) => {
                  item.role = newValue;
                }
              "
              libelle=""
            />
          </div>
        </template>
      </list-grid>


      <div
        v-if="
          beneficiariesToAdd.length > 0 && beneficiariesToAddSort.length === 0
        "
        class="flex flex-col justify-center items-center w-full"
      >
        <h3
          class="text-pink-600 w-full text-center font-semibold text-lg mt-12"
        >
          Aucun résultat !
        </h3>
        <h4 class="text-indigo-600 w-4/6 text-center text-base mt-8 mb-16">
          Aucun résultat de recherche, veuillez élargir votre recherche en
          diminuant vos critères
        </h4>
        <div>
          <illustration name="no_result" />
        </div>
      </div>
      <div
        v-if="beneficiaries.length == 0"
        class="flex flex-col justify-center items-center w-full"
      >
        <h3
          class="text-pink-600 w-full text-center font-semibold text-lg mt-12"
        >
          Aucun bénéficiaire
        </h3>
        <h4 class="text-indigo-600 w-4/6 text-center text-base mt-8 mb-16">
          Il n'y a aucun bénéficiaire inscrit dans votre structure
        </h4>
        <div>
          <illustration name="no_result" />
        </div>
      </div>
      <div
        v-if="beneficiariesToAdd.length == 0"
        class="flex flex-col justify-center items-center w-full"
      >
        <h3
          class="text-pink-600 w-full text-center font-semibold text-lg mt-12"
        >
          Aucun bénéficiaire
        </h3>
        <h4 class="text-indigo-600 w-4/6 text-center text-base mt-8 mb-16">
          Il n'y a plus de bénéficiaire à gérer pour ce référent
        </h4>
        <div>
          <illustration name="no_result" />
        </div>
      </div>

      <!-- BEGIN list of referents -->
      <div
        v-if="saving"
        class="w-full flex justify-center items-center"
      >
        <div
          class="border rounded-full h-3 w-3 mr-1 sm:h-4 sm:w-4 sm:mr-1.5 border-t-2 border-r-2 border-l-0 border-b-0 border-gray-900 border-opacity-25 animate-spin"
        />
      </div>

      <div
        class="flex flex-row justify-center items-center space-x-4 mt-10 text-lg"
        v-if="beneficiariesToAdd.length > 0"
      >
        <span @click="previousPage()">
          <icon
            name="chevron-left"
            class="h-4 w-2"
            :class="{
              'text-gray-600': numPage == 1,
              'text-indigo-600 cursor-pointer': numPage > 1,
            }"
          />
        </span>
        <div>
          <span
            :class="{
              'text-gray-600': numPage == 1,
              'text-indigo-600': numPage > 1,
            }"
          >{{ numPage }}</span>
          <span
            :class="{
              'text-gray-600': numPage == 1 || numPage == totalPages,
              'text-indigo-600': numPage > 1 && numPage < totalPages,
            }"
          >/</span>
          <span
            :class="{
              'text-gray-600': numPage == totalPages,
              'text-indigo-600': numPage < totalPages,
            }"
          >{{ totalPages }}</span>
        </div>
        <span @click="nextPage()">
          <icon
            name="chevron-right"
            class="h-4 w-2"
            :class="{
              'text-gray-600': numPage == totalPages,
              'text-indigo-600 cursor-pointer': numPage < totalPages,
            }"
          />
        </span>
      </div>

      <div
        class="flex flex-row place-content-center mt-10"
        v-if="beneficiariesToAdd.length > 0"
      >
        <action-button
          libelle="AJOUTER"
          border-color="border-transparent"
          text-color="text-white"
          bg-color="bg-pink-600"
          bg-color-square="bg-pink-600"
          icon-color="text-white"
          :uppercase="true"
          icon="plus-circle"
          :right="true"
          :spinner="saving"
          @clickAction="saving ? null : addBeneficiariesToReferent()"
        />
      </div>
    </div>
    <!-- END Content modal add beneficiary to a referent -->
  </popup>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import Popup from './../../../components/Popup';
import Icon from './../../../components/Icon';
import MessageHeader from './../../../components/MessageHeader';
import Illustration from './../../../components/Illustration';
import ActionButton from './../../../components/ActionButton';
import InputSelect from './../../../components/Inputs/InputSelect';
import SortButton from './../../../components/SortButton';
import CheckboxIndigo from '../../../components/Inputs/CheckboxIndigo';

import Constants from './../../../constants';
import ProfilePicture from './../../../components/ProfilePicture.vue';
import SearchBar from '../../../components/Inputs/SearchBar.vue';
import ListGrid from '../../../components/AccordionTable/ListGrid.vue';

export default {
  name: 'AddBeneficiaryReferent',

  props: {
    dataToShow: Boolean,
  },

  data() {
    return {
      searchBeneficiary: '',
      sort: 'ABC',
      numPage: 1,
    };
  },

  components: {
    ListGrid,
    SearchBar,
    ProfilePicture,
    Popup,
    Icon,
    Illustration,
    ActionButton,
    InputSelect,
    MessageHeader,
    SortButton,
    CheckboxIndigo,
  },

  computed: {
    ...mapGetters({
      messageHeader: 'permissions/messageHeader',
      saving: 'permissions/saving',
      referentToEdit: 'referents/referentToEdit',
      beneficiaries: 'beneficiaries/beneficiaries',
      beneficiariesToAdd: 'beneficiariesReferent/beneficiariesToAdd',
      selectedBeneficiaries: 'beneficiariesReferent/selectedBeneficiaries',
      roles: 'roles/roles',
    }),

    constants: function () {
      return Constants;
    },

    listRoles: function () {
      let list = [];

      this.roles
        .filter((role) => {
          return ![
            Constants.ROLE_ADMIN_PAYEUR,
            Constants.ROLE_ADMIN_ENTREPRISE,
            Constants.ROLE_BENEFICIAIRE,
          ].includes(role.name);
        })
        .forEach((role) => {
          list.push({ value: role.display_name });
        });
      list.push({ value: '_', disabled: true });
      list.push({ value: 'Accès spécifiques' });

      return list;
    },

    //Recherche bénéficiaire à gérer
    beneficiariesToAddFilter: {
      get: function () {
        let filteredList = [];

        filteredList = [...this.beneficiariesToAdd].filter((beneficiary) =>
          (beneficiary.firstname + ' ' + beneficiary.lastname)
            .toLocaleLowerCase()
            .includes(this.searchBeneficiary.toLocaleLowerCase())
        );

        return filteredList;
      },
    },

    //tri bénéficiaire
    beneficiariesToAddSort() {
      let sortedList = [];

      if (['ABC', 'ZYX'].includes(this.sort)) {
        sortedList = [...this.beneficiariesToAddFilter].sort(function (a, b) {
          let nameA = a.lastname.toUpperCase();
          let nameB = b.lastname.toUpperCase();
          return nameA.localeCompare(nameB);
        });
      }

      if (['JAN', 'DEC'].includes(this.sort)) {
        sortedList = [...this.beneficiariesToAddFilter].sort(function (a, b) {
          let dateA = a.created_at;
          let dateB = b.created_at;
          return dateA.localeCompare(dateB);
        });
      }

      if (['ABC', 'JAN'].includes(this.sort)) {
        return sortedList;
      }
      if (['ZYX', 'DEC'].includes(this.sort)) {
        return sortedList.reverse();
      }
    },

    beneficiariesPaginated() {
      let begin = 5 * (this.numPage - 1);
      let end =
        begin + 5 > this.beneficiariesToAdd.length
          ? this.beneficiariesToAdd.length
          : begin + 5;

      return this.beneficiariesToAddSort.slice(begin, end);
    },

    totalPages() {
      return Math.ceil(this.beneficiariesToAddSort.length / 5);
    },
  },

  methods: {
    ...mapActions({
      addBeneficiariesToReferent:
        'beneficiariesReferent/addBeneficiariesToReferent',
      selectAllBeneficiaries: 'beneficiariesReferent/selectAllBeneficiaries',
      selectBeneficiary: 'beneficiariesReferent/selectBeneficiary',
    }),

    ...mapMutations({
      SET_ERRORS: 'permissions/SET_ERRORS',
      SET_SAVING: 'permissions/SET_SAVING',
      SET_SELECTEDBENEFICIARIES:
        'beneficiariesReferent/SET_SELECTEDBENEFICIARIES',
    }),

    changeToShow(val) {
      this.$emit('showChange', val);
      this.SET_SELECTEDBENEFICIARIES([]);
      this.SET_ERRORS(null);
      this.SET_SAVING(false);
    },

    confirmCreateReferent() {
      this.createReferent()
        .then((isCreated) => {
          if (isCreated) {
            this.changeToShow(2);
          }
        })
        .catch((error) => {
          this.changeToShow(100);
        });
    },

    previousPage() {
      if (this.numPage > 1) this.numPage--;
    },

    nextPage() {
      if (this.numPage < this.totalPages) this.numPage++;
    },
  },
};
</script>
